/**
 * Flattens the token objects into CSS custom properties.
 *
 * Everything resolves through variables rather than being compiled into Tailwind classes and an
 * MUI palette separately. That is what lets the two apps share a brand: the Tailwind preset and
 * the MUI theme both emit `var(--pos-action-base)`, so there is one value at runtime and a theme
 * switch is a class on `<html>`, not a rebuild.
 */

import { chartDark, chartLight } from '../primitives/chart.js';
import {
  elevationDark,
  elevationLight,
  shadowDark,
  shadowLight,
  focusRing,
} from '../primitives/elevation.js';
import { duration, easing, pressScale, transition } from '../primitives/motion.js';
import { density, posColumn, radius, space, touchTarget, zIndex } from '../primitives/layout.js';
import {
  fontFamily,
  fontWeight,
  numericFeatures,
  typeScale,
} from '../primitives/typography.js';
import { darkColors, lightColors } from '../semantic/themes.js';

export const PREFIX = '--pos';

const kebab = (value: string) =>
  value
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    // Fractional spacing steps: `space[0.5]` becomes `--pos-space-0-5`.
    .replace(/\./g, '-')
    .toLowerCase();

type Nested = { readonly [key: string]: string | number | Nested };

/** `{ action: { base: '#…' } }` becomes `--pos-action-base: #…`. */
function flatten(value: Nested, path: readonly string[] = []): Record<string, string> {
  const out: Record<string, string> = {};

  for (const [key, entry] of Object.entries(value)) {
    const next = [...path, kebab(key)];

    if (typeof entry === 'object') {
      Object.assign(out, flatten(entry, next));
    } else {
      out[`${PREFIX}-${next.join('-')}`] = String(entry);
    }
  }

  return out;
}

/** Tokens that do not change with the theme. Emitted once, on `:root`. */
export function staticVariables(): Record<string, string> {
  return {
    ...flatten({ font: fontFamily }),
    ...flatten({ weight: fontWeight }),
    ...flatten({ numeric: numericFeatures }),
    ...flatten({ space }),
    ...flatten({ density }),
    ...flatten({ radius }),
    ...flatten({ touch: touchTarget }),
    ...flatten({ column: posColumn }),
    ...flatten({ z: zIndex }),
    ...flatten({ duration }),
    ...flatten({ ease: easing }),
    ...flatten({ transition }),
    ...flatten({ focus: { width: focusRing.width, offset: focusRing.offset } }),
    [`${PREFIX}-press-scale`]: pressScale,
    // Each type step becomes four variables, so a component can set size and line height without
    // reaching for a class that bundles both.
    ...flatten({
      text: Object.fromEntries(
        Object.entries(typeScale).map(([name, step]) => [
          name,
          {
            size: step.size,
            leading: step.lineHeight,
            weight: step.weight,
            tracking: step.tracking,
          },
        ]),
      ),
    }),
  };
}

/** Tokens that flip with the theme. Emitted twice, under each theme selector. */
export function themeVariables(theme: 'light' | 'dark'): Record<string, string> {
  const colors = theme === 'light' ? lightColors : darkColors;
  const shadow = theme === 'light' ? shadowLight : shadowDark;
  const elevation = theme === 'light' ? elevationLight : elevationDark;
  // Not part of `ThemeColors`, and deliberately so: shop themes override `ThemeColors` roles, and
  // a series that moved with the shop's look would make the same category two colours in two
  // shops. Flipping with light/dark is the only variation a chart colour is allowed.
  const chart = theme === 'light' ? chartLight : chartDark;

  return {
    ...flatten(colors),
    ...flatten({ shadow }),
    ...flatten({ elevation }),
    ...flatten({ chart }),
    // A real declaration, not a custom property: this is what makes the browser's own scrollbars,
    // form controls and autofill follow the theme instead of staying light on a dark page.
    'color-scheme': theme,
  };
}

/** The variable name for a role path, for use in an adapter. `cssVar('action', 'base')`. */
export const cssVar = (...path: readonly string[]) =>
  `${PREFIX}-${path.map(kebab).join('-')}`;

/** The same, wrapped for use as a value. */
export const v = (...path: readonly string[]) => `var(${cssVar(...path)})`;
